import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { SaleRow } from "@sable/api-contract";
import { IconBox } from "../components/icons.tsx";
import { Empty, ErrorBlock, Loading } from "../components/ui.tsx";
import { t, tk } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { money, shortDate } from "../lib/format.ts";
import { keys, useMarketplaces, useSales, useUnmatchedSales } from "../lib/queries.ts";
import { useCan, useWs, wsPath } from "../lib/workspace.tsx";

function Row({ sale }: { sale: SaleRow }) {
  const ws = useWs();
  const finance = useCan("finance.read");
  const mps = useMarketplaces();
  const mp = mps.data?.find((m) => m.code === sale.marketplace)?.name ?? sale.marketplace;
  return (
    <Link className="sale-row" to={wsPath(ws, `/items/${sale.itemId}`)}>
      <div className="mini">{sale.thumbUrl ? <img src={sale.thumbUrl} alt="" loading="lazy" /> : <div className="thumb-empty"><IconBox /></div>}</div>
      <div className="grow">
        <div className="name">{sale.title || t("inventory.untitled")}</div>
        <div className="muted small">{mp} · {shortDate(sale.soldAt)} · {sale.sku}</div>
      </div>
      <div className="amount">
        <div className="big">{money(sale.salePriceMinor, sale.currency)}</div>
        {finance && sale.netProfitMinor !== null && (
          <div className={`small ${sale.netProfitMinor >= 0 ? "pos" : "neg"}`}>{t("sales.profit", { amount: money(sale.netProfitMinor, sale.currency) })}</div>
        )}
      </div>
    </Link>
  );
}

/** Sales a paired browser saw on a marketplace that tie to no item for certain. */
function UnmatchedCard() {
  const ws = useWs();
  const qc = useQueryClient();
  const mps = useMarketplaces();
  const q = useUnmatchedSales(ws.id);
  if (!q.data?.length) return null;
  const dismiss = async (id: string) => {
    await request(`/unmatched-sales/${id}/dismiss`, { method: "POST", workspace: ws.id });
    void qc.invalidateQueries({ queryKey: keys.unmatched(ws.id) });
  };
  return (
    <section className="card stack">
      <div>
        <h2>{t("sales.unmatched")}</h2>
        <p className="muted small">{t("sales.unmatchedHint")}</p>
      </div>
      <div className="list">
        {q.data.map((u) => (
          <div className="list-row" key={u.id}>
            <div className="grow">
              <div className="name">{u.title || u.externalOrderId}</div>
              <div className="sub">
                {mps.data?.find((m) => m.code === u.marketplace)?.name ?? u.marketplace} · {shortDate(u.soldAt)} · {tk("sales.unmatchedReason", u.reason)}
              </div>
            </div>
            <span className="num">{money(u.priceMinor, u.currency)}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => void dismiss(u.id)}>{t("sales.dismiss")}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Sales() {
  const canSell = useCan("sales.write");
  const ws = useWs();
  const pending = useSales(ws.id, "pending");
  const all = useSales(ws.id, "all");
  return (
    <div className="stack-lg">
      <div className="page-head"><h1>{t("sales.title")}</h1></div>
      {canSell && <UnmatchedCard />}
      <section className="card">
        <h2>{t("sales.toShip")}</h2>
        {pending.isPending ? <Loading /> : pending.isError ? <ErrorBlock error={pending.error} /> :
          pending.data.length === 0 ? <p className="muted small">{t("sales.nothingToShip")}</p> :
          <div>{pending.data.map((s) => <Row key={s.saleId} sale={s} />)}</div>}
      </section>
      <section className="card">
        <h2>{t("sales.recent")}</h2>
        {all.isPending ? <Loading /> : all.isError ? <ErrorBlock error={all.error} /> :
          all.data.length === 0 ? <Empty title={t("sales.empty")} /> :
          <div>{all.data.map((s) => <Row key={s.saleId} sale={s} />)}</div>}
      </section>
    </div>
  );
}
